import type { FilterDefinition, LeadCatalogResponse } from "@gioia365/contracts";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useId, useLayoutEffect, useMemo, useRef, useState, type JSX } from "react";
import { Button } from "../../design/components/Button";
import { Icon } from "../../design/components/Icon";
import { formatNumericDate } from "../../i18n/format";
import { useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { FilterPanel } from "./FilterPanel";
import { filterLabelQueries, filterValueLabels } from "./filter-queries";
import { conditionText, fieldOf, filterFields, type FilterField } from "./filter-text";
import { definitionOf, draftOf, serializeFilter, type DraftCondition } from "./filter-url";
import "./filters.css";

// Il pulsante «Filtri» e i filtri attivi (piano M3 Task 4, mock-up `setupFilterBar`; UX_SYSTEM, «Sistema filtri»): il
// pulsante con il numero delle condizioni apre il pannello a destra; fuori restano soltanto i filtri attivi, ognuno con
// il suo valore e la ✕, e «Azzera i filtri». La bozza del pannello sta qui, anche con le scelte incomplete; ogni
// condizione completa arriva subito a `onChange`, che la scrive nell'indirizzo: la tabella si aggiorna senza «Applica».

type Props = {
  /** I filtri dell'indirizzo: soltanto condizioni complete. */
  definition: FilterDefinition;
  onChange(next: FilterDefinition): void;
  onReset(): void;
  /** I campi dell'azienda (`GET /api/leads/catalog`); finché arrivano il pannello ha quelli di sistema. */
  catalog: LeadCatalogResponse | undefined;
  tab: TabContext;
  /** Il numero dei risultati, ripetuto nel pannello: mentre è aperto la pagina dietro è inerte. */
  resultText?: string;
  /** La ricerca o «Di chi» restringono già l'elenco: i conteggi delle voci contano tutti i Lead visibili e lo dicono. */
  narrowed?: boolean;
};

/** Un giorno di calendario (`AAAA-MM-GG`) non ha fuso: resta quel giorno ovunque. */
const formatDay = (day: string) => formatNumericDate("UTC", day);

export function FilterBar({ definition, onChange, onReset, catalog, tab, resultText, narrowed = false }: Props): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const panelId = useId();
  const countId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const fields = useMemo(() => filterFields(t, catalog), [t, catalog]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  // La bozza del pannello parte dall'indirizzo e lo segue quando cambia da fuori («Indietro», «Azzera ricerca e
  // filtri»). L'indirizzo scritto da qui arriva un momento dopo, e con scelte veloci (un numero scritto cifra per cifra)
  // arrivano anche i passaggi intermedi: `pending` li ricorda, così la bozza, già più avanti e con le scelte
  // incomplete, non torna indietro.
  const current = serializeFilter(definition);
  const [draft, setDraft] = useState<DraftCondition[]>(() => draftOf(definition));
  const [synced, setSynced] = useState(current);
  const [pending, setPending] = useState<ReadonlyArray<string | undefined>>([]);
  if (current !== synced) {
    setSynced(current);
    const step = pending.indexOf(current);
    if (serializeFilter(definitionOf(draft)) === current) {
      setPending([]);
    } else if (step !== -1) {
      setPending(pending.slice(step + 1));
    } else {
      setDraft(draftOf(definition));
      setPending([]);
    }
  }

  // I nomi delle voci scelte che la scheda non ricorda ancora (all'apertura della pagina, o quando l'indirizzo cambia da
  // fuori): l'elenco intero delle voci per owner, status e tag, i soli codici scelti per città e province (decisione
  // P20). Ogni lettura li ricorda (`rememberFilterLabels`), e la pagina si ridisegna quando arrivano.
  useQueries({ queries: filterLabelQueries(queryClient, tab, definition.conditions) });

  /** I nomi delle voci di un campo: quelli che l'interfaccia conosce, poi quelli ricordati per la scheda. */
  function labelsFor(field: FilterField): ReadonlyMap<string, string> {
    // Le voci della bozza e quelle dell'indirizzo, che per un momento possono essere diverse.
    const values = [...draft, ...definition.conditions].flatMap((condition) => (condition.field === field.ref && "values" in condition ? condition.values : []));
    return filterValueLabels(t, queryClient, tab, field.ref, values, catalog);
  }

  // Chiuso il pannello (Esc, «Fatto», un clic fuori), il fuoco torna sul pulsante «Filtri», anche quando lo aveva
  // aperto un filtro attivo.
  const wasOpen = useRef(false);
  useLayoutEffect(() => {
    if (wasOpen.current && !open) buttonRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  function update(next: DraftCondition[]) {
    const before = serializeFilter(definitionOf(draft));
    setDraft(next);
    const nextDefinition = definitionOf(next);
    const written = serializeFilter(nextDefinition);
    if (written === before) return;
    setPending((previous) => [...previous, written]);
    onChange(nextDefinition);
  }

  function openAt(ref: string | null) {
    setExpanded(ref);
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setExpanded(null);
  }

  function remove(ref: string) {
    update(draft.filter((condition) => condition.field !== ref));
    buttonRef.current?.focus();
  }

  function reset() {
    setDraft([]);
    setPending((previous) => [...previous, undefined]);
    onReset();
  }

  function resetFromBar() {
    reset();
    // «Azzera i filtri» sparisce con i filtri: il fuoco torna su «Filtri».
    buttonRef.current?.focus();
  }

  const active = definition.conditions;
  const activeText = active.length === 1 ? t("leadFilters.activeOne") : t("leadFilters.activeMany", { count: active.length });

  return (
    <div className="g-fbar" role="group" aria-label={t("leadFilters.button")}>
      <Button
        ref={buttonRef}
        className="g-fbar__button"
        data-on={active.length > 0 ? "true" : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-describedby={active.length > 0 ? countId : undefined}
        onClick={() => openAt(null)}
      >
        <Icon name="filter" />
        {t("leadFilters.button")}
        {active.length > 0 ? (
          <span className="g-fbar__count" aria-hidden="true">
            {active.length}
          </span>
        ) : null}
      </Button>
      {active.length > 0 ? (
        <span id={countId} className="g-visually-hidden">
          {activeText}
        </span>
      ) : null}
      {active.map((condition) => {
        const field = fieldOf(t, fields, condition, catalog);
        return (
          <span key={condition.field} className="g-fbar__chip">
            <button type="button" className="g-fbar__chip-open" aria-haspopup="dialog" onClick={() => openAt(condition.field)}>
              <span>{t("leadFilters.chipField", { field: field.label })}</span>{" "}
              <strong>{conditionText(t, field, condition, labelsFor(field), formatDay)}</strong>
            </button>
            <button type="button" className="g-fbar__x" aria-label={t("leadFilters.remove", { field: field.label })} onClick={() => remove(condition.field)}>
              <Icon name="close" />
            </button>
          </span>
        );
      })}
      {active.length > 0 ? (
        <Button variant="ghost" onClick={resetFromBar}>
          {t("leadFilters.reset")}
        </Button>
      ) : null}
      {open ? (
        <FilterPanel
          tab={tab}
          panelId={panelId}
          fields={fields}
          draft={draft}
          expanded={expanded}
          onExpand={setExpanded}
          onDraftChange={update}
          onReset={reset}
          onClose={close}
          labelsFor={labelsFor}
          formatDay={formatDay}
          resultText={resultText}
          narrowed={narrowed}
        />
      ) : null}
    </div>
  );
}
